import { useEffect, useRef } from 'react';
import { authClient } from '../lib/auth-client';

const REDDIT_PIXEL_ID = 'a2_ibc1395njc1t';
const REDDIT_CLICK_ID_COOKIE = 'reddit_click_id';
const COOKIE_DOMAIN = '.insightanalytics.io';
const COOKIE_EXPIRY_DAYS = 30;

/**
 * Get a cookie value by name
 */
const getCookie = (name: string): string | null => {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Set a cookie with cross-subdomain support
 */
const setCookie = (name: string, value: string, days: number): void => {
  try {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; domain=${COOKIE_DOMAIN}; path=/; SameSite=Lax`;
  } catch (e) {
    // Silently fail
  }
};

/**
 * Delete a cookie
 */
const deleteCookie = (name: string): void => {
  try {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${COOKIE_DOMAIN}; path=/`;
  } catch (e) {
    // Silently fail
  }
};

// Capture Reddit click ID immediately on module load (before any React renders/redirects)
// This runs synchronously when the JS bundle loads
// Also captures from URL in case user lands directly on app.insightanalytics.io
const captureRedditClickId = (): void => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const rdtClickId = urlParams.get('rdt_cid');

    if (rdtClickId) {
      setCookie(REDDIT_CLICK_ID_COOKIE, rdtClickId, COOKIE_EXPIRY_DAYS);
    }
  } catch (e) {
    // Silently fail
  }
};

// Run immediately
captureRedditClickId();

/**
 * Check if the current user came from a Reddit ad
 * Returns true if we have a Reddit click ID cookie (set by Framer or app)
 */
export const isRedditAttributed = (): boolean => {
  return !!getCookie(REDDIT_CLICK_ID_COOKIE);
};

/**
 * Get the stored Reddit click ID (if any)
 */
export const getRedditClickId = (): string | null => {
  return getCookie(REDDIT_CLICK_ID_COOKIE);
};

/**
 * Clear Reddit attribution (e.g., after conversion is tracked)
 */
export const clearRedditAttribution = (): void => {
  deleteCookie(REDDIT_CLICK_ID_COOKIE);
};

interface RedditPixelIdentifierProps {
  children: React.ReactNode;
}

export const RedditPixelIdentifier = ({
  children,
}: RedditPixelIdentifierProps) => {
  const { data: session } = authClient.useSession();
  const hasInitialized = useRef(false);
  const lastUserId = useRef<string | null>(null);

  // Initialize Reddit Pixel with user match keys
  useEffect(() => {
    if (!window.rdt) return;

    const currentUserId = session?.user?.id ?? null;

    // Skip if we've already initialized for this user (or anonymous)
    if (hasInitialized.current && lastUserId.current === currentUserId) {
      return;
    }

    if (session?.user) {
      // User is authenticated - initialize with match keys
      window.rdt('init', REDDIT_PIXEL_ID, {
        email: session.user.email,
        externalId: session.user.id,
      });
    } else {
      // Anonymous user - initialize without match keys
      window.rdt('init', REDDIT_PIXEL_ID);
    }

    // Track page visit after init
    window.rdt('track', 'PageVisit');

    hasInitialized.current = true;
    lastUserId.current = currentUserId;
  }, [session]);

  return <>{children}</>;
};
